import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { RouterOutlet, RouterModule, Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';

import { ToolbarModule } from '@openng/optimus-ui/toolbar';
import { ButtonModule } from '@openng/optimus-ui/button';
import { Drawer } from '@openng/optimus-ui/drawer';
import { AutoCompleteModule } from '@openng/optimus-ui/autocomplete';
import { FormsModule } from '@angular/forms';
import { ThemeService } from '../../services/theme.service';
import { SearchService } from '../../services/search.service';
import { UiStateService } from '../../services/ui-state.service';
import { SearchResults } from '../../models/search.types';
import { debounceTime, Subject, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface SearchResultItem {
  kind: 'org' | 'user' | 'session';
  id: number | string;
  label: string;
  sublabel?: string;
  orgId?: number | null;
  sessionType?: 'LIVE' | 'PRACTICE';
}

export interface SearchGroup {
  label: string;
  items: SearchResultItem[];
}

@Component({
  selector: 'app-layout',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    RouterModule,
    ToolbarModule,
    ButtonModule,
    Drawer,
    AutoCompleteModule,
    FormsModule,
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
})
export class LayoutComponent {
  authService = inject(AuthService);
  themeService = inject(ThemeService);
  private router = inject(Router);
  private searchService = inject(SearchService);
  private uiState = inject(UiStateService);

  mobileMenuOpen = signal(false);

  // Single source for both the desktop sidebar and the mobile drawer.
  protected readonly navGroups = [
    {
      label: 'Overview',
      items: [{ path: '/dashboard', label: 'Dashboard', icon: 'pi pi-home' }],
    },
    {
      label: 'Customers',
      items: [
        { path: '/organizations', label: 'Organizations', icon: 'pi pi-building' },
        { path: '/users', label: 'Users', icon: 'pi pi-users' },
        { path: '/pricing-plans', label: 'Pricing & Plans', icon: 'pi pi-tag' },
        { path: '/support-tickets', label: 'Support Tickets', icon: 'pi pi-ticket' },
        { path: '/feedback', label: 'Feedback', icon: 'pi pi-comments' },
      ],
    },
    {
      label: 'Product',
      items: [
        { path: '/sessions', label: 'Sessions', icon: 'pi pi-history' },
        { path: '/practice-courses', label: 'Practice Courses', icon: 'pi pi-graduation-cap' },
        { path: '/llm', label: 'LLM', icon: 'pi pi-bolt' },
        { path: '/research', label: 'Research', icon: 'pi pi-book' },
      ],
    },
    {
      label: 'Growth',
      items: [
        { path: '/tracking', label: 'Tracking & Affiliates', icon: 'pi pi-chart-line' },
        { path: '/waitlist', label: 'Waitlist', icon: 'pi pi-list' },
      ],
    },
    {
      label: 'Developers',
      items: [{ path: '/api-docs', label: 'API Docs', icon: 'pi pi-file' }],
    },
  ];
  protected searchGroups = signal<SearchGroup[]>([]);
  protected searchLoading = signal(false);
  protected selectedResult: SearchResultItem | null = null;
  private searchSubject = new Subject<string>();

  constructor() {
    this.searchSubject
      .pipe(
        debounceTime(350),
        switchMap((q) => {
          const term = q.trim();
          if (!term) {
            return of<SearchResults>({ organizations: [], users: [], sessions: [] });
          }
          this.searchLoading.set(true);
          return this.searchService
            .search(term)
            .pipe(
              catchError(() => of<SearchResults>({ organizations: [], users: [], sessions: [] })),
            );
        }),
        takeUntilDestroyed(),
      )
      .subscribe((results) => {
        this.searchGroups.set(this.toSearchGroups(results));
        this.searchLoading.set(false);
      });
  }

  toggleTheme() {
    this.themeService.toggleTheme();
  }

  toggleMobileMenu() {
    this.mobileMenuOpen.update((v) => !v);
  }

  closeMobileMenu() {
    this.mobileMenuOpen.set(false);
  }

  logout() {
    this.authService.logout();
    this.closeMobileMenu();
  }

  onSearch(event: { query: string }) {
    const q = event.query?.trim() ?? '';
    if (!q) {
      this.searchGroups.set([]);
      return;
    }
    this.searchSubject.next(q);
  }

  private toSearchGroups(results: SearchResults): SearchGroup[] {
    const groups: SearchGroup[] = [];
    if (results.organizations.length) {
      groups.push({
        label: 'Organizations',
        items: results.organizations.map((o) => ({
          kind: 'org' as const,
          id: o.id,
          label: o.name,
          sublabel: 'Organization',
        })),
      });
    }
    if (results.users.length) {
      groups.push({
        label: 'Users',
        items: results.users.map((u) => ({
          kind: 'user' as const,
          id: u.id,
          label: u.name,
          sublabel: `${u.email} · ${u.organisationName || 'No org'}`,
          orgId: u.organisationId,
        })),
      });
    }
    if (results.sessions.length) {
      groups.push({
        label: 'Sessions',
        items: results.sessions.map((s) => ({
          kind: 'session' as const,
          id: s.id,
          label: s.title || s.id,
          sublabel:
            `${s.type} · ${s.userName || ''} ${s.organisationName ? '· ' + s.organisationName : ''}`.trim(),
          sessionType: s.type,
        })),
      });
    }
    return groups;
  }

  onResultSelected(event: { value: SearchGroup | SearchResultItem }) {
    const item = event.value;
    if ('kind' in item) {
      this.selectedResult = null;
      this.searchGroups.set([]);

      if (item.kind === 'org') {
        this.router.navigate(['/organizations', item.id]);
      } else if (item.kind === 'user') {
        if (item.orgId != null) {
          this.router.navigate(['/organizations', item.orgId], {
            queryParams: { highlightUser: item.id },
          });
        } else {
          this.uiState.setSessionUserFilter(Number(item.id));
          this.router.navigate(['/sessions']);
        }
      } else if (item.kind === 'session') {
        const type = (item.sessionType || 'live').toLowerCase();
        this.router.navigate(['/sessions', type, item.id]);
      }
    }
  }
}
